import { useEffect, useState } from 'react'
import { useGetMediatorAccessTokenFn, useGetStreamingUrlFn } from '../../../../api/apiQueries'
import { LoggerName, namedLogger } from '../../../../utils/logger'
import { createAuthenticatedFlvUrl, getHlsStreamingUrl } from './utils/playbackUtils'
import type { EngagementClip, EngagementRecordings } from './types'
import { batchedPromiseAllSettled } from '../../../../utils/utils'
import { getFlvVideoDuration, getNativeVideoDuration } from './utils/fetchVideoDurationsUtils'

const logger = namedLogger(LoggerName.useClipMediaDurations)

// Minimum interval between onProgress reports while a video clip is being evaluated for duration.
// Bounds how often the timeline re-renders per growing clip.
const FLV_PROGRESS_INTERVAL_MS = 300

// Pre-fetch default for an FLV clip: assume almost no footage until the duration fetch proves more exists.
export const DEFAULT_FLV_CLIP_DURATION_SECONDS = 1

// Cap on how long a single video fetch may stream before it's cut off (keeping whatever duration the scan
// revealed). Guards against a mediator response that never terminates - without it such a clip would sit
// Generous because an FLVs can download the whole file while HLS fetches are typically much lighter.
const CLIP_FETCH_TIMEOUT_MS = 60_000

/**
 * Merge grown durations into the durations map, keeping every entry only increasing (never below the 1s seed).
 * Returns the input map UNCHANGED (same instance) when nothing actually grew, so state consumers can
 * skip a no-op update. Exported for tests.
 */
export const mergeGrownDurations = (durations: Map<string, number>, grownSecondsByRecordingId: Map<string, number>): Map<string, number> => {
  let next: Map<string, number> | null = null
  for (const [recordingId, durationSeconds] of grownSecondsByRecordingId) {
    const previous = durations.get(recordingId) ?? DEFAULT_FLV_CLIP_DURATION_SECONDS
    if (durationSeconds > previous) {
      next ??= new Map(durations)
      next.set(recordingId, durationSeconds)
    }
  }
  return next ?? durations
}

/**
 * Fetch each clip's real duration because the `startTime` and `endTime` times returned by
 * `/recordings` api claim there's more footage than there actually is in the video sources.
 *
 * Unmounting aborts every in-flight fetch. Feed the result through applyClipMediaDurations so consumers
 * only ever see footage that exists.
 *
 * @returns Map from recordingId to duration seconds, growing while fetches run.
 */
export const useClipMediaDurations = (
  recordings: EngagementRecordings | undefined,
  locationId: string | undefined,
  engagementId: string | undefined
): Map<string, number> => {
  const getMediatorAccessToken = useGetMediatorAccessTokenFn()
  const getStreamingUrl = useGetStreamingUrlFn()
  const [durationsByRecordingId, setDurationsByRecordingId] = useState<Map<string, number>>(new Map())

  useEffect(() => {
    if (!recordings || !locationId || !engagementId) return

    const flattenedRecordingsForAllCameras = Array.from(recordings.recordingsByCameraId.values()).flat()

    const flvClips: EngagementClip[] = []
    const hlsClips: EngagementClip[] = []
    for (const clip of flattenedRecordingsForAllCameras) {
      // FLV is preferred for playback when present, otherwise HLS is used. (we never use playback/dash)
      if (clip.links['playback/flv']?.href) {
        flvClips.push(clip)
      } else if (clip.links['playback/hls']?.href) {
        hlsClips.push(clip)
      }
    }

    if (flvClips.length === 0 && hlsClips.length === 0) return

    let cancelled = false

    // Seed every FLV clip at the default before any network activity so the timeline starts minimal.
    // HLS clips are deliberately unseeded - see the hook doc.
    setDurationsByRecordingId(new Map(flvClips.map(clip => [clip.recordingId, DEFAULT_FLV_CLIP_DURATION_SECONDS])))

    // Growth reports are buffered and flushed as ONE state update per interval, so N clips probing in
    // parallel cost one recordings rebuild + timeline re-render per tick instead of N. Durations never
    // shrink, and never drop below the 1s default (mergeGrownDurations enforces both). Both the
    // in-progress reports and each clip duration fetch's final result go through this same gate.
    const pendingGrowthByRecordingId = new Map<string, number>()
    let flushTimer: ReturnType<typeof setTimeout> | undefined

    const flushGrowth = () => {
      clearTimeout(flushTimer)
      flushTimer = undefined
      if (cancelled || pendingGrowthByRecordingId.size === 0) return
      const grown = new Map(pendingGrowthByRecordingId)
      pendingGrowthByRecordingId.clear()
      setDurationsByRecordingId(prev => mergeGrownDurations(prev, grown))
    }

    const growDuration = (recordingId: string, durationSeconds: number) => {
      if (cancelled) return
      const pending = pendingGrowthByRecordingId.get(recordingId)
      if (pending === undefined || durationSeconds > pending) {
        pendingGrowthByRecordingId.set(recordingId, durationSeconds)
      }
      if (!flushTimer) {
        flushTimer = setTimeout(flushGrowth, FLV_PROGRESS_INTERVAL_MS)
      }
    }

    // One AbortController per clip fetch: the deadline aborts only its own stream, the cleanup aborts them all.
    const abortControllers: AbortController[] = []

    const fetchClipDurationWithTimeout = async (recordingId: string, fetchClipDuration: (signal: AbortSignal) => Promise<number | null>) => {
      const abortController = new AbortController()
      abortControllers.push(abortController)
      const deadline = setTimeout(() => abortController.abort(), CLIP_FETCH_TIMEOUT_MS)
      try {
        const finalSeconds = await fetchClipDuration(abortController.signal)
        if (finalSeconds !== null) growDuration(recordingId, finalSeconds)
      } catch (e) {
        logger.error(`Failed to fetch clip duration for ${recordingId}`, e as Error)
      }
      clearTimeout(deadline)
    }

    const fetchAllClipDurations = async () => {
      try {
        const { accessToken } = await getMediatorAccessToken({ locationId, engagementId })
        if (cancelled) return

        const asyncClipPromises = [
          ...flvClips.map(clip => {
            return () => {
              const flvUrl = clip.links['playback/flv']?.href
              if (!flvUrl) return Promise.resolve()
              return fetchClipDurationWithTimeout(clip.recordingId, async signal => {
                const finalUrl = createAuthenticatedFlvUrl(flvUrl)
                const duration = await getFlvVideoDuration(finalUrl, accessToken, signal, loadedDuration =>
                  growDuration(clip.recordingId, loadedDuration)
                )
                return duration
              })
            }
          }),
          ...hlsClips.map(clip => {
            return () => {
              const hlsUrl = clip.links['playback/hls']?.href
              if (!hlsUrl) return Promise.resolve()
              return fetchClipDurationWithTimeout(clip.recordingId, async signal => {
                const streamingSessionUrl = await getHlsStreamingUrl(accessToken, hlsUrl, getStreamingUrl)
                if (streamingSessionUrl) {
                  const duration = await getNativeVideoDuration(streamingSessionUrl, signal, loadedDuration =>
                    growDuration(clip.recordingId, loadedDuration)
                  )
                  return duration
                } else {
                  throw new Error('No streaming session URL found in response for clip')
                }
              })
            }
          })
        ]
        const results = await batchedPromiseAllSettled(asyncClipPromises, 10)
        results.forEach(result => {
          if (result.status === 'rejected') {
            // A single clip's duration correction was rejected; other clips are unaffected, so warn.
            logger.warn('Failed to fetch clip duration', result.reason)
          }
        })
      } catch (e) {
        // This only corrects inaccuracies in the clip durations. If something throws, it doesn't
        // prevent the rest of the clips from being used. Clips keep their last known duration, so warn.
        logger.error('Failed to fetch all clip durations', e as Error)
      }
    }
    fetchAllClipDurations()
    return () => {
      cancelled = true
      clearTimeout(flushTimer)
      // Stop streaming immediately on unmount / recordings change - these downloads can be large.
      for (const abortController of abortControllers) {
        abortController.abort()
      }
    }
  }, [recordings, locationId, engagementId, getMediatorAccessToken, getStreamingUrl])

  return durationsByRecordingId
}
